import React, { useState } from "react";
import axios from "axios";

// AddComment: supports both Public Comment (place_name) and Private Note (prediction_id)
const AddComment = ({
  placeName: propPlaceName,
  responseData,
  predictionId: propPredictionId,
  disabled = false,
  onSuccess,
}) => {
  const API_URL = process.env.REACT_APP_API_URL;

  // responseData can be an object (with id, location) or a string (location)
  const derivedPlaceName =
    propPlaceName ||
    (typeof responseData === "string" ? responseData : responseData?.location);
  const predictionId =
    propPredictionId ||
    (typeof responseData === "object" ? responseData?.id : undefined);

  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);
  const [isPublicComment, setIsPublicComment] = useState(true);
  const [saveStatus, setSaveStatus] = useState({ message: "", type: "" });

  const extractErrorMessage = (error) => {
    const detail = error?.response?.data?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
      return detail.map((d) => d?.msg || JSON.stringify(d)).join(" | ");
    if (detail && typeof detail === "object")
      return detail.message || detail.error || JSON.stringify(detail);
    return (
      error?.response?.data?.message || error?.message || "Bilinmeyen hata"
    );
  };

  const handleSend = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSaveStatus({ message: "", type: "" });

    if (content.trim() === "") {
      setLoading(false);
      return alert("Lütfen içeriği girin.");
    }

    const token = localStorage.getItem("token");
    if (!token) {
      setLoading(false);
      return alert("Giriş yapmalısınız.");
    }

    try {
      if (isPublicComment) {
        if (!derivedPlaceName) {
          setLoading(false);
          return alert("Yorum eklemek için önce bir mekan analiz edin.");
        }

        // Attempt 1: { place_name, comment }
        try {
          const res = await axios.post(
            `${API_URL}/add_comment`,
            { place_name: derivedPlaceName, comment: content },
            {
              headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
              },
            }
          );
          if (res.status !== 200) throw res;
        } catch (err) {
          // If 422, retry with { place_name, comment_text }
          if (err?.response?.status === 422) {
            const res2 = await axios.post(
              `${API_URL}/add_comment`,
              { place_name: derivedPlaceName, comment_text: content },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
            if (res2.status !== 200) throw res2;
          } else {
            throw err;
          }
        }

        setSaveStatus({
          message: "Yorumunuz başarıyla gönderildi!",
          type: "success",
        });
        setContent("");
        if (typeof onSuccess === "function") onSuccess();
      } else {
        // Private note
        if (!predictionId) {
          setLoading(false);
          return alert("Not eklemek için önce bir tahmin yapmalısınız.");
        }
        const res = await axios.post(
          `${API_URL}/add_note`,
          { prediction_id: predictionId, note: content },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (res.status !== 200) throw res;

        setSaveStatus({
          message: "Notunuz başarıyla kaydedildi! (Gizli)",
          type: "success",
        });
        setContent("");
      }

      setTimeout(() => setSaveStatus({ message: "", type: "" }), 3000);
    } catch (error) {
      const errorMsg = extractErrorMessage(error);
      setSaveStatus({ message: `Hata: ${errorMsg}`, type: "error" });
    } finally {
      setLoading(false);
    }
  };

  const isTextareaDisabled =
    disabled ||
    loading ||
    (isPublicComment && !derivedPlaceName) ||
    (!isPublicComment && !predictionId);

  const isButtonDisabled =
    disabled ||
    loading ||
    content.trim() === "" ||
    (isPublicComment && !derivedPlaceName) ||
    (!isPublicComment && !predictionId);

  return (
    <div className="bg-gray-700 p-5 rounded-xl shadow-2xl border border-gray-600 space-y-4">
      <h3 className="text-xl font-bold text-white flex items-center space-x-2">
        <span role="img" aria-label="chat">
          💬
        </span>
        <span>
          {isPublicComment
            ? "Herkese Açık Yorumunuz"
            : "Kişisel Notunuz (Sadece Size Özel)"}
        </span>
      </h3>

      <div className="relative">
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          className="w-full p-4 pr-3 text-gray-200 bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 resize-none placeholder-gray-500"
          rows={4}
          placeholder={
            isPublicComment
              ? derivedPlaceName
                ? `${derivedPlaceName} hakkında düşüncelerinizi yazın...`
                : "Yorum eklemek için önce bir mekan analiz edin"
              : predictionId
              ? "Gizli kişisel notunuzu buraya yazın..."
              : "Not eklemek için önce bir tahmin yapın"
          }
          disabled={isTextareaDisabled}
        />
      </div>

      <div className="flex justify-between items-center pt-2 border-t border-gray-600/50">
        <label className="flex items-center space-x-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!isPublicComment}
            onChange={() => setIsPublicComment(!isPublicComment)}
            className="form-checkbox h-5 w-5 text-indigo-500 bg-gray-900 border-gray-700 rounded focus:ring-indigo-500"
          />
          <span
            className={`text-sm font-medium ${
              isPublicComment ? "text-blue-400" : "text-indigo-400"
            }`}
          >
            {isPublicComment ? "Herkese Açık Yorum" : "Gizli Not"}
          </span>
        </label>

        <button
          onClick={handleSend}
          disabled={isButtonDisabled}
          className="px-5 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed transition-colors duration-200 shadow-md"
        >
          {loading
            ? "Gönderiliyor..."
            : isPublicComment
            ? "Yorumu Gönder"
            : "Notu Kaydet"}
        </button>
      </div>

      {disabled && (
        <div className="text-xs text-gray-400">
          Misafir kullanıcı yorum veya not ekleyemez.
        </div>
      )}

      {saveStatus.message && (
        <div
          className={`mt-3 p-3 rounded-lg text-sm font-medium ${
            saveStatus.type === "success"
              ? "bg-green-800/50 text-green-300"
              : "bg-red-800/50 text-red-300"
          }`}
        >
          {saveStatus.message}
        </div>
      )}
    </div>
  );
};

export default AddComment;
